import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import ToggleSwitch from '../common/ToggleSwitch';
import { ICON_STROKE_WIDTH } from '../../constants';
import { ESCALATION_CONTACT_ROLES } from '../../constants/escalationContact.constants';
import type {
  EscalationContact,
  EscalationContactFormValues,
} from '../../types/escalationContact.types';
import {
  normalizeMobileInput,
  validateEscalationContactForm,
} from '../../utils/escalationContactValidation';

interface EscalationContactFormDialogProps {
  open: boolean;
  inline?: boolean;
  mode: 'add' | 'edit';
  initialContact?: EscalationContact | null;
  saving?: boolean;
  serverErrors?: Record<string, string>;
  onClose: () => void;
  onSubmit: (values: EscalationContactFormValues) => void;
}

const EMPTY_FORM: EscalationContactFormValues = {
  name: '',
  role: '',
  department: '',
  mobileNumber: '',
  alternateMobileNumber: '',
  active: true,
};

export default function EscalationContactFormDialog({
  open,
  inline = false,
  mode,
  initialContact,
  saving = false,
  serverErrors = {},
  onClose,
  onSubmit,
}: Readonly<EscalationContactFormDialogProps>) {
  const [values, setValues] = useState<EscalationContactFormValues>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!open) {
      return;
    }
    if (mode === 'edit' && initialContact) {
      setValues({
        name: initialContact.name,
        role: initialContact.role,
        department: initialContact.department || '',
        mobileNumber: initialContact.mobileNumber,
        alternateMobileNumber: initialContact.alternateMobileNumber || '',
        active: initialContact.status === 'Active',
      });
    } else {
      setValues(EMPTY_FORM);
    }
    setErrors({});
  }, [open, mode, initialContact]);

  useEffect(() => {
    if (!open || inline) {
      return undefined;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open, inline]);

  const mergedErrors = { ...errors, ...serverErrors };

  const handleChange = <K extends keyof EscalationContactFormValues>(
    field: K,
    value: EscalationContactFormValues[K]
  ) => {
    setValues(prev => ({ ...prev, [field]: value }));
    if (errors[field as string] || serverErrors[field as string]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field as string];
        return next;
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const nextErrors = validateEscalationContactForm(values);
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }
    onSubmit({
      ...values,
      name: values.name.trim(),
      department: values.department.trim(),
      mobileNumber: normalizeMobileInput(values.mobileNumber),
      alternateMobileNumber: normalizeMobileInput(values.alternateMobileNumber),
    });
  };

  if (!open) {
    return null;
  }

  const drawer = (
    <aside
      className={['esc-contact-drawer', inline ? 'esc-contact-drawer--inline' : '']
        .filter(Boolean)
        .join(' ')}
      role="dialog"
      aria-modal={inline ? 'false' : 'true'}
      aria-labelledby="esc-contact-drawer-title"
    >
      <header className="esc-contact-drawer-header">
        <div>
          <h2
            id="esc-contact-drawer-title"
            className="esc-contact-drawer-title"
          >
            {mode === 'add' ? 'Add contact' : 'Edit contact'}
          </h2>
          <p className="esc-contact-drawer-subtitle">
            Recipient details for critical-result escalations.
          </p>
        </div>
        <button
          type="button"
          className="esc-contact-drawer-close"
          aria-label="Close"
          disabled={saving}
          onClick={onClose}
        >
          <X
            size={18}
            strokeWidth={ICON_STROKE_WIDTH}
          />
        </button>
      </header>

      <form
        className={[
          'esc-contact-form',
          'esc-contact-drawer-form',
          saving ? 'esc-contact-form--saving' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onSubmit={handleSubmit}
        aria-busy={saving}
      >
        <div className="esc-contact-drawer-body">
          <label className="esc-field">
            <span className="esc-field-label">
              Name <span className="esc-required">*</span>
            </span>
            <input
              className={['esc-input', mergedErrors.name ? 'esc-input--error' : '']
                .filter(Boolean)
                .join(' ')}
              value={values.name}
              onChange={e => handleChange('name', e.target.value)}
              placeholder="Dr. Full Name"
              readOnly={saving}
            />
            {mergedErrors.name && <span className="esc-field-error">{mergedErrors.name}</span>}
          </label>

          <label className="esc-field">
            <span className="esc-field-label">
              Role <span className="esc-required">*</span>
            </span>
            <div className="esc-select-wrap">
              <select
                className={['esc-input', 'esc-select', mergedErrors.role ? 'esc-input--error' : '']
                  .filter(Boolean)
                  .join(' ')}
                value={values.role}
                onChange={e => handleChange('role', e.target.value)}
              >
                <option value="">Select role</option>
                {ESCALATION_CONTACT_ROLES.map(role => (
                  <option
                    key={role.value}
                    value={role.value}
                  >
                    {role.label}
                  </option>
                ))}
              </select>
            </div>
            {mergedErrors.role && <span className="esc-field-error">{mergedErrors.role}</span>}
          </label>

          <label className="esc-field">
            <span className="esc-field-label">Department (optional)</span>
            <input
              className="esc-input"
              value={values.department}
              onChange={e => handleChange('department', e.target.value)}
              placeholder="e.g. Radiology"
              readOnly={saving}
            />
          </label>

          <label className="esc-field">
            <span className="esc-field-label">
              Mobile number <span className="esc-required">*</span>
            </span>
            <input
              className={['esc-input', mergedErrors.mobileNumber ? 'esc-input--error' : '']
                .filter(Boolean)
                .join(' ')}
              value={values.mobileNumber}
              onChange={e => handleChange('mobileNumber', normalizeMobileInput(e.target.value))}
              placeholder="+91 9XXXX XXXXX"
              inputMode="numeric"
              maxLength={10}
              readOnly={saving}
            />
            {mergedErrors.mobileNumber && (
              <span className="esc-field-error">{mergedErrors.mobileNumber}</span>
            )}
          </label>

          <label className="esc-field">
            <span className="esc-field-label">Alternate mobile (optional)</span>
            <input
              className={['esc-input', mergedErrors.alternateMobileNumber ? 'esc-input--error' : '']
                .filter(Boolean)
                .join(' ')}
              value={values.alternateMobileNumber}
              onChange={e =>
                handleChange('alternateMobileNumber', normalizeMobileInput(e.target.value))
              }
              placeholder="+91 9XXXX XXXXX"
              inputMode="numeric"
              maxLength={10}
              readOnly={saving}
            />
            {mergedErrors.alternateMobileNumber && (
              <span className="esc-field-error">{mergedErrors.alternateMobileNumber}</span>
            )}
          </label>

          <div className="esc-field esc-field--toggle">
            <div className="esc-active-toggle-row">
              <div>
                <span className="esc-field-label">Active</span>
                <p className="esc-field-helper">Only active contacts appear in the matrix.</p>
              </div>
              <ToggleSwitch
                checked={values.active}
                disabled={saving}
                onChange={checked => handleChange('active', checked)}
              />
            </div>
          </div>
        </div>

        <div className="esc-contact-drawer-footer">
          <button
            type="button"
            className="esc-btn esc-btn--secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="esc-btn esc-btn--primary"
            disabled={saving}
          >
            {saving ? 'Saving…' : mode === 'add' ? 'Save contact' : 'Save changes'}
          </button>
        </div>
      </form>
    </aside>
  );

  if (inline) {
    return drawer;
  }

  return (
    <>
      <button
        type="button"
        className="esc-contact-drawer-backdrop"
        aria-label="Close add contact panel"
        onClick={() => !saving && onClose()}
      />
      {drawer}
    </>
  );
}
